/**
 * App theme tokens.
 * Change DEFAULT_THEME.primary for the factory default.
 * Runtime color is controlled from Store Settings → Appearance.
 */
export const DEFAULT_THEME = {
    primary: '#00b761',
    primaryHover: '#00a354',
    primarySoft: '#e8f8ef',
    primarySoftStrong: '#def5de',
    primaryBorder: '#9adfbb',
    primarySelectedBg: '#dff6ea',
    primarySelectedHover: '#d2f1e1',
    black: '#111827',
    blackMuted: '#1c1f23',
};

/** Mutable current theme — updated by applyPrimaryColor / ThemeProvider */
export const THEME = { ...DEFAULT_THEME };

export const THEME_STORAGE_KEY = 'onesync_theme_primary';

export const THEME_PRESETS = [
    { name: 'Green', value: '#00b761' },
    { name: 'Emerald', value: '#059669' },
    { name: 'Mint', value: '#10b981' },
    { name: 'Teal', value: '#0d9488' },
    { name: 'Olive', value: '#4d7c0f' },
    { name: 'Black', value: '#111827' },
];

const clampByte = (n) => Math.max(0, Math.min(255, Math.round(n)));

export function normalizeHex(input) {
    if (!input || typeof input !== 'string') return null;
    let hex = input.trim().replace(/^#/, '');
    if (/^[0-9a-fA-F]{3}$/.test(hex)) {
        hex = hex
            .split('')
            .map((c) => c + c)
            .join('');
    }
    if (!/^[0-9a-fA-F]{6}$/.test(hex)) return null;
    return `#${hex.toLowerCase()}`;
}

export function isValidHex(input) {
    return Boolean(normalizeHex(input));
}

function hexToRgb(hex) {
    const normalized = normalizeHex(hex);
    if (!normalized) return { r: 0, g: 183, b: 97 };
    return {
        r: parseInt(normalized.slice(1, 3), 16),
        g: parseInt(normalized.slice(3, 5), 16),
        b: parseInt(normalized.slice(5, 7), 16),
    };
}

function rgbToHex({ r, g, b }) {
    return `#${[r, g, b]
        .map((v) => clampByte(v).toString(16).padStart(2, '0'))
        .join('')}`;
}

function mixRgb(a, b, t) {
    return {
        r: a.r + (b.r - a.r) * t,
        g: a.g + (b.g - a.g) * t,
        b: a.b + (b.b - a.b) * t,
    };
}

/** Build a full palette from a single primary hex */
export function deriveThemeFromPrimary(primaryHex) {
    const primary = normalizeHex(primaryHex) || DEFAULT_THEME.primary;
    const base = hexToRgb(primary);
    const white = { r: 255, g: 255, b: 255 };
    const black = { r: 0, g: 0, b: 0 };

    return {
        primary,
        primaryHover: rgbToHex(mixRgb(base, black, 0.12)),
        primarySoft: rgbToHex(mixRgb(base, white, 0.88)),
        primarySoftStrong: rgbToHex(mixRgb(base, white, 0.82)),
        primaryBorder: rgbToHex(mixRgb(base, white, 0.55)),
        primarySelectedBg: rgbToHex(mixRgb(base, white, 0.85)),
        primarySelectedHover: rgbToHex(mixRgb(base, white, 0.8)),
        black: DEFAULT_THEME.black,
        blackMuted: DEFAULT_THEME.blackMuted,
    };
}

function getThemeStorageKey() {
    try {
        const auth = JSON.parse(localStorage.getItem('AuthData') || '{}');
        const storeId = auth?.storeId;
        return storeId ? `${THEME_STORAGE_KEY}_${storeId}` : THEME_STORAGE_KEY;
    } catch {
        return THEME_STORAGE_KEY;
    }
}

export function getStoredPrimaryColor() {
    try {
        const key = getThemeStorageKey();
        const stored = normalizeHex(localStorage.getItem(key));
        if (stored) return stored;

        const storeDetails = JSON.parse(localStorage.getItem('storeDeatils') || '{}');
        return normalizeHex(storeDetails?.themePrimaryColor);
    } catch {
        return null;
    }
}

function persistPrimaryColor(primary) {
    try {
        localStorage.setItem(getThemeStorageKey(), primary);
        const storeDetails = JSON.parse(localStorage.getItem('storeDeatils') || '{}');
        localStorage.setItem(
            'storeDeatils',
            JSON.stringify({
                ...storeDetails,
                themePrimaryColor: primary,
            })
        );
    } catch {
        // ignore quota / private mode
    }
}

export function buildThemeCssVars(theme = THEME) {
    return {
        '--primary-color': theme.primary,
        '--primary-hover': theme.primaryHover,
        '--primary-soft': theme.primarySoft,
        '--primary-soft-strong': theme.primarySoftStrong,
        '--primary-border': theme.primaryBorder,
        '--primary-selected-bg': theme.primarySelectedBg,
        '--primary-selected-hover': theme.primarySelectedHover,
        '--primary-black': theme.black,
        '--primary-black-muted': theme.blackMuted,
        '--brand-green': theme.primary,
        '--brand-green-hover': theme.primaryHover,
        '--brand-green-soft': theme.primarySoft,
        '--brand-green-border': theme.primaryBorder,
        '--brand-selected-bg': theme.primarySelectedBg,
        '--brand-selected-hover': theme.primarySelectedHover,
    };
}

/** @deprecated prefer buildThemeCssVars — kept for older imports */
export const THEME_CSS_VARS = buildThemeCssVars(DEFAULT_THEME);

export function applyThemeVars(theme = THEME, root = typeof document !== 'undefined' ? document.documentElement : null) {
    if (!root) return;
    Object.entries(buildThemeCssVars(theme)).forEach(([key, value]) => {
        root.style.setProperty(key, value);
    });
}

/**
 * Apply a primary color across CSS vars + mutable THEME.
 * @returns {string} normalized primary hex
 */
export function applyPrimaryColor(primaryHex, { persist = true } = {}) {
    const next = deriveThemeFromPrimary(primaryHex);
    Object.assign(THEME, next);
    applyThemeVars(THEME);
    if (persist) persistPrimaryColor(THEME.primary);
    return THEME.primary;
}

export function resetPrimaryColor({ persist = true } = {}) {
    return applyPrimaryColor(DEFAULT_THEME.primary, { persist });
}

/** Boot: restore saved color or factory default */
export function initThemeFromStorage() {
    const stored = getStoredPrimaryColor();
    applyPrimaryColor(stored || DEFAULT_THEME.primary, { persist: false });
}

export default THEME;
